import FeatherIcon from 'components/base/FeatherIcon';
import { ReactNode } from 'react';
import { Link } from 'react-router';

interface Props {
  label: string;
  value: ReactNode;
  /** Small line beneath the value (e.g. `Current epoch`). */
  sublabel?: ReactNode;
  /** When set, the whole card becomes a link; an arrow-up-right icon shows in
   *  the top-right corner. */
  linkTo?: string;
  /** Like linkTo, to another site (opens in a new tab). */
  href?: string;
  /** Monospace value - right for numbers; turn off for words. */
  mono?: boolean;
  /** Let a long sublabel wrap instead of truncating. */
  wrapSublabel?: boolean;
  /** Needs attention: the value in warning / danger. */
  tone?: 'warning' | 'danger';
  /** A threshold it is judged against: met (a green check and outline,
   *  the usual background) or not (a red cross). */
  check?: boolean | null;
  /** A met check keeps the usual border (the check icon alone). */
  plainCheck?: boolean;
  /** Something is wrong right now (e.g. the cluster halted): the whole
   *  card in the danger tint, the value in its emphasis shade. */
  alert?: boolean;
}

/**
 * Bordered stat tile used in KPI strips across the app.
 *
 *   ┌──────────────────────┐
 *   │ label              ↗ │  (arrow only when `linkTo` is set)
 *   │ 1,234                │
 *   │ sublabel             │
 *   └──────────────────────┘
 */
const StatCard = ({
  label,
  value,
  sublabel,
  linkTo,
  href,
  mono = true,
  wrapSublabel = false,
  tone,
  check,
  plainCheck = false,
  alert = false
}: Props) => {
  const body = (
    <>
      <div className="d-flex justify-content-between align-items-start">
        <p className="text-body-tertiary fs-10 mb-1 mt-0">{label}</p>
        {check != null ? (
          <FeatherIcon
            icon={check ? 'check-circle' : 'x-circle'}
            size={14}
            className={check ? 'text-success' : 'text-danger'}
            aria-label={check ? 'Met' : 'Not met'}
          />
        ) : (
          (linkTo || href) && (
            <FeatherIcon
              icon="arrow-up-right"
              size={12}
              className="text-body-tertiary"
            />
          )
        )}
      </div>
      {/* Truncate rather than overflow the card; the full value stays
          available on hover when it is plain text. */}
      <p
        className={`fs-7 fw-bolder mb-0 text-truncate${mono ? ' font-monospace' : ''}${alert ? ' text-danger-emphasis' : tone ? ` text-${tone}` : ''}`}
        title={typeof value === 'string' ? value : undefined}
      >
        {value}
      </p>
      {sublabel && (
        <p
          className={`text-body-tertiary fs-10 mb-0 mt-1${wrapSublabel ? '' : ' text-truncate'}`}
          title={typeof sublabel === 'string' ? sublabel : undefined}
        >
          {sublabel}
        </p>
      )}
    </>
  );
  const frame = alert
    ? 'bg-danger-subtle border-danger-subtle'
    : check === true && !plainCheck
      ? 'bg-body-emphasis border-success'
      : check === false
        ? 'bg-body-emphasis border-danger-subtle'
        : 'bg-body-emphasis border-translucent';
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className={`stat-card d-block position-relative overflow-hidden rounded border ${frame} px-3 py-2 h-100 text-decoration-none text-body`}
      >
        {body}
      </a>
    );
  }
  if (linkTo) {
    return (
      <Link
        to={linkTo}
        className={`stat-card d-block position-relative overflow-hidden rounded border ${frame} px-3 py-2 h-100 text-decoration-none text-body`}
      >
        {body}
      </Link>
    );
  }
  return (
    <div
      className={`stat-card position-relative overflow-hidden rounded border ${frame} px-3 py-2 h-100`}
    >
      {body}
    </div>
  );
};

export default StatCard;
